import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@store/index';
import { apiClient, getAccessToken, refreshAccessToken, setAccessToken } from '@services/api';

function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthStore();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // REV-732: Profile and Settings go to URLs, so they are links. What the
  // anchor adds is cmd-click, middle-click, "open in new tab" and a status-bar
  // preview of the destination, none of which a click handler can offer.
  //
  // onClick={handleClose} stays on the items, and the reason is worth keeping.
  //
  // Measured here, it is redundant: remove it and the menu still closes, with
  // #root left clean. I removed it on that basis — and the same reasoning
  // applied to OrgSwitcher turned out to be WRONG. There the anchor navigates
  // without the Menu closing, leaving MUI's modal aria-hidden="true" on #root,
  // so every control on the new page is absent from the accessibility tree
  // while visibly present. An e2e caught it; no unit test could.
  //
  // Two near-identical components, opposite behaviour, and I could not predict
  // which from reading them. The handler costs nothing where it is redundant
  // and prevents a total accessibility failure where it is not.
  //
  // REV-491: Profile is a tab of Settings now. /profile still redirects, but
  // linking straight to the real destination avoids a pointless hop.
  const PROFILE_HREF = '/settings/profile';
  const SETTINGS_HREF = '/settings';

  // REV-802: end the session on the server as well, or the refresh cookie
  // could still mint a new token. The endpoint needs a bearer token, and after
  // a reload none is held yet, so renew one first. If the server cannot be
  // reached, sign out locally anyway: the user asked to leave.
  const handleLogout = async () => {
    handleClose();
    try {
      if (!getAccessToken()) await refreshAccessToken();
      await apiClient.post('/v1/auth/logout');
    } catch {
      // Best effort; the local sign-out below still happens.
    }
    setAccessToken(null);
    logout();
    void navigate('/login');
  };

  // If not authenticated, show login button area
  if (!isAuthenticated || !user) {
    return (
      <IconButton
        // REV-732: goes to /login, so it is a link.
        component={RouterLink}
        to="/login"
        aria-label={t('navigation.login')}
        color="inherit"
        sx={{ ml: 1 }}
      >
        <PersonIcon />
      </IconButton>
    );
  }

  // Get user initials for avatar
  const getInitials = (firstName: string, lastName: string): string => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return `${first}${last}`.toUpperCase() || '??';
  };

  // Get full name for display
  const getFullName = (firstName: string, lastName: string): string => {
    return `${firstName || ''} ${lastName || ''}`.trim() || 'User';
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        aria-label={t('navigation.userMenu')}
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        // REV-525: must be PRESENT (as "false") when closed, not omitted —
        // ARIA-04. Omitting it leaves assistive tech unable to tell the control
        // is collapsible at all.
        aria-expanded={open}
        sx={{ ml: 1 }}
      >
        {/* REV-525: STRUCTURE-23 — the initials are decoration on top of the
            aria-label, not a second name. Exposing both made the button carry
            two competing labelling strategies. */}
        <Avatar
          aria-hidden
          sx={{
            // REV-537 (CSS-06 / WCAG 1.4.4 Resize Text): rem, not px. MUI's
            // Avatar sets overflow:hidden to clip to a circle, so a fixed pixel
            // box cannot grow with the user's text size and the initials are
            // simply cut off. 2.25rem is the same 36px at the default root size
            // and scales from there.
            width: '2.25rem',
            height: '2.25rem',
            bgcolor: 'secondary.main',
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
        >
          {getInitials(user.first_name, user.last_name)}
        </Avatar>
      </IconButton>

      <Menu
        // REV-525 (ARIA-04): the id belongs on the MenuList, not the Menu. MUI
        // puts a Menu-level id on the popover paper (a div), so the trigger's
        // aria-controls pointed at something that is not role="menu". The
        // accessible name has to go through MenuListProps for the same reason —
        // it is the MenuList that carries the role.
        MenuListProps={{ id: 'user-menu', 'aria-label': t('navigation.userMenu') }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              minWidth: 220,
              mt: 1,
            },
          },
        }}
      >
        {/* User Info Header */}
        <Box sx={{ px: 2, py: 1.5 }}>
          {/* component="p": MUI maps subtitle1 to <h6>, which made the menu
              skip H1 -> H6 (STRUCTURE-08 / WCAG 1.3.1). This is the signed-in
              user's name — a label identifying the menu's owner, not a section
              heading — so it should not be in the heading outline at all.
              Styling is unchanged; only the element changes. */}
          <Typography variant="subtitle1" component="p" fontWeight={600}>
            {getFullName(user.first_name, user.last_name)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </Box>

        <Divider />

        {/* Menu Items */}
        <MenuItem component={RouterLink} to={PROFILE_HREF} onClick={handleClose}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('navigation.profile')} />
        </MenuItem>

        <MenuItem component={RouterLink} to={SETTINGS_HREF} onClick={handleClose}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('navigation.settings')} />
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => void handleLogout()}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('navigation.logout')} />
        </MenuItem>
      </Menu>
    </>
  );
}

export default UserMenu;
